/**
 * Route table and auth guards.
 *
 * Public: marketing landing, signup wizard, login, OAuth return.
 * Guarded: `/post-auth` (persona dispatch), `/admin`, `/app/fanatic-portal` and
 * the tenant-scoped `/app/{tenant_slug}/…` views, all wrapped in the app shell.
 *
 * A guard sends an unauthenticated visitor to `/login?next=…` so the deep link
 * survives sign-in, and a persona mismatch is answered with the persona's own
 * landing view rather than a dead end.
 */
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

// The platform-admin portal is a separate audience (a handful of staff) with
// its own heavy dependencies (recharts in the billing dashboard): loading it
// lazily keeps all of that out of the customer-facing main chunk.
const AdminPortal = lazy(() => import('../admin/admin-shell'));
// The two game-day boards (Play Entry, Coaches Highlight) share the layout
// platform - drag/resize editor, widget catalog, config panels, formation
// glyph renderer - which the rest of the app never touches. Split so a
// coach opening the roster or a fanatic on the portal never downloads it.
const PlayEntryPage = lazy(() =>
  import('../pages/play-entry/play-entry').then((m) => ({ default: m.PlayEntryPage }))
);
const CoachesHighlightPage = lazy(() =>
  import('../pages/highlight/coaches-highlight').then((m) => ({ default: m.CoachesHighlightPage }))
);
const routeLoading = <div className="route-loading" aria-busy="true" />;
import {
  APP_VIEWS,
  FANATIC_INVITE_PARAM,
  FANATIC_VIEWS,
  ROUTES,
  TENANT_SLUG_PATTERN,
} from '../lib/constants';
import { resolvePersonaRoute, type Persona } from '../lib/personas';
import { HomePage } from '../pages/app/home';
import { PlayArchivePage } from '../pages/archive/play-archive';
import { ForgotPasswordPage } from '../pages/auth/forgot-password';
import { LoginPage } from '../pages/auth/login';
import { OAuthCallbackPage } from '../pages/auth/oauth-callback';
import { ResetPasswordPage } from '../pages/auth/reset-password';
import { SignupWizard } from '../pages/auth/signup-wizard';
import { BillingPortal } from '../pages/billing/billing-portal';
import { EventDetailPage } from '../pages/events/event-detail';
import { EventManagerPage } from '../pages/events/event-manager';
import { FanaticAccountSettingsPage } from '../pages/fanatic/account-settings';
import { FanaticBrowseFollowPage } from '../pages/fanatic/browse-follow';
import { FanLoginPage } from '../pages/fanatic/fan-login';
import { FanSignupPage } from '../pages/fanatic/fan-signup';
import { FanaticGameSummaryPage } from '../pages/fanatic/fanatic-game-summary';
import { FanaticHomePage, FanaticPortalPage } from '../pages/fanatic/fanatic-portal';
import { FanaticLiveVideoPage } from '../pages/fanatic/live-video';
import { DocsPage } from '../pages/help/docs-page';
import { IntegrationsPage } from '../pages/integrations/integrations-page';
import { LiveVideoPage } from '../pages/live-video/live-video';
import { LandingPage } from '../pages/marketing/landing';
import { PrivacyPage, TermsPage } from '../pages/marketing/legal';
import { PricingPage } from '../pages/marketing/pricing';
import { NotFoundPage } from '../pages/not-found';
import { SetupWizardPage } from '../pages/onboarding/setup-wizard';
import { PlayEntryHelpPage } from '../pages/play-entry/play-entry-help';
import { PlaybookPage } from '../pages/playbook/playbook';
import { RosterManagerPage } from '../pages/roster/roster-manager';
import { PreferencesPage } from '../pages/settings/preferences';
import { GameSummaryPage } from '../pages/summary/game-summary';
import { CustomStatFieldsPage } from '../pages/team-admin/custom-stat-fields';
import { FanaticManagementPage } from '../pages/team-admin/fanatic-management';
import { IdentityProvidersPage } from '../pages/team-admin/identity-providers';
import { UserManagementPage } from '../pages/team-admin/user-management';
import { TeamSettings } from '../pages/team-settings/team-settings';
import { StatTrackerPage } from '../pages/tracker/stat-tracker';

import { useSession } from './session-context';
import { AppShell } from './shell';
import { TenantRouter } from './tenant-router';
import { canonicalTenantSlug, tenantMismatchDestination } from './tenant-slug';

/**
 * The tenant slug in a `/app/{slug}/…` path, or `null` when the path is not a
 * tenant deep link. The static `/app/…` siblings (`/app/docs`,
 * `/app/fanatic-portal`) are not team codes and are excluded.
 */
export function tenantSlugFromPath(pathname: string): string | null {
  const match = /^\/app\/([^/]+)/.exec(pathname);
  if (match === null) return null;
  const candidate = match[1].toLowerCase();
  const reserved = [ROUTES.docs, ROUTES.fanaticPortal].map((route) => route.split('/').pop());
  if (reserved.includes(candidate)) return null;
  return TENANT_SLUG_PATTERN.test(candidate) ? candidate : null;
}

/** Blocks a route until there is a session that has satisfied MFA. */
export function RequireAuth({
  children,
  allowedPersonas,
}: {
  children: JSX.Element;
  allowedPersonas?: readonly Persona[];
}): JSX.Element {
  const { isAuthenticated, isMfaPending, isBootstrapping, persona, tenantSlug } = useSession();
  const location = useLocation();

  if (isBootstrapping) return <p role="status">Restoring your session…</p>;
  if (!isAuthenticated || isMfaPending || persona === null) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    // A tenant deep link (`/app/{slug}/…`) also carries the team code so the
    // login page resolves the team on mount and opens on credentials — staff
    // should not have to retype a code that is already in the URL.
    const teamSlug = tenantSlugFromPath(location.pathname);
    const teamParam = teamSlug === null ? '' : `&team=${encodeURIComponent(teamSlug)}`;
    return <Navigate to={`${ROUTES.login}?next=${next}${teamParam}`} replace />;
  }
  if (allowedPersonas !== undefined && !allowedPersonas.includes(persona)) {
    return <Navigate to={resolvePersonaRoute(persona, tenantSlug)} replace />;
  }
  return children;
}

/**
 * Refuses to mount the tenant shell for a team code the session cannot open.
 *
 * Sits inside `RequireAuth` on `/app/:tenantSlug`. The gateway scopes every
 * API call to the token's tenant (or the Fanatic grant in `X-Tenant-ID`), so
 * without this check `/app/other-slug` would render "other-slug" in the
 * header and nav while the body listed the session's own team — mixed tenant
 * chrome (docs/TENANT_ISOLATION.md, "URL slug validation"). The redirect
 * keeps the session: staff land on the same view under their own team code
 * (or `/post-auth` when sign-in captured no team), Fanatics on their portal.
 */
export function RequireTenantAccess({ children }: { children: JSX.Element }): JSX.Element {
  const { tenantSlug: routeSlug } = useParams();
  const { persona, accessibleTenantSlugs, canAccessTenantSlug } = useSession();
  const location = useLocation();

  const canonical = canonicalTenantSlug(routeSlug);
  if (canonical === null || !canAccessTenantSlug(canonical)) {
    return (
      <Navigate
        replace
        to={tenantMismatchDestination({
          persona,
          accessibleTenantSlugs,
          pathname: location.pathname,
          search: location.search,
        })}
      />
    );
  }
  // Canonicalise the address bar so links built from the slug all agree.
  if (routeSlug !== canonical) {
    const rest = location.pathname.slice(`/app/${routeSlug}`.length);
    return <Navigate replace to={`/app/${canonical}${rest}${location.search}`} />;
  }
  return children;
}

/**
 * The Fanatic funnel's public deep links (`/fanatic/browse`,
 * `/fanatic/accept?invite_token=…`) forward into the portal so every fanatic
 * screen runs inside one shell — and therefore one team context. The guard on
 * the portal bounces an anonymous visitor to `/login?next=…` first, so an
 * emailed invite link still works end to end.
 */
function FanaticEntryRedirect({ view }: { view: string }): JSX.Element {
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  // `/fanatic/accept?token=…` is tolerated alongside the documented spelling.
  const legacyToken = search.get('token');
  if (legacyToken !== null && !search.has(FANATIC_INVITE_PARAM)) {
    search.delete('token');
    search.set(FANATIC_INVITE_PARAM, legacyToken);
  }
  const suffix = search.toString();
  return (
    <Navigate replace to={`${ROUTES.fanaticPortal}/${view}${suffix === '' ? '' : `?${suffix}`}`} />
  );
}

export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      <Route path={ROUTES.landing} element={<LandingPage />} />
      <Route path={ROUTES.signup} element={<SignupWizard />} />
      <Route path={ROUTES.pricing} element={<PricingPage />} />
      <Route path={ROUTES.terms} element={<TermsPage />} />
      <Route path={ROUTES.privacy} element={<PrivacyPage />} />
      <Route path={ROUTES.login} element={<LoginPage />} />
      {/* The fan funnel's own door (Zone 1). Separate from the staff /login
          because a fan has no team code and never needs MFA. */}
      <Route path={ROUTES.fanLogin} element={<FanLoginPage />} />
      <Route path={ROUTES.fanSignup} element={<FanSignupPage />} />
      {/* A trimmed `/fans` URL lands on the fan signup page rather than a
          not-found: it is the fan funnel's front door for anyone new. */}
      <Route path={ROUTES.fans} element={<Navigate to={ROUTES.fanSignup} replace />} />
      {/* Self-service password reset (Zone 1). Public: the reset email deep
          link must work in a fresh browser with no session. */}
      <Route path={ROUTES.forgotPassword} element={<ForgotPasswordPage />} />
      <Route path={ROUTES.resetPassword} element={<ResetPasswordPage />} />
      <Route path={ROUTES.oauthCallback} element={<OAuthCallbackPage />} />
      <Route
        path={ROUTES.postAuth}
        element={
          <RequireAuth>
            <TenantRouter />
          </RequireAuth>
        }
      />

      {/*
       * Platform Admin console (FR-ADMIN-01..06). A SEPARATE route tree with its
       * own shell — it is cross-tenant, so it must not inherit the tenant chrome
       * or the persona nav. The guard here only requires a session: the
       * platform_admin check happens inside `AdminPortal`, so a coach who
       * follows an /admin link gets an explicit denial instead of a silent
       * redirect (AC-ADMIN-01).
       */}
      <Route
        path={`${ROUTES.admin}/*`}
        element={
          <RequireAuth>
            <Suspense fallback={routeLoading}>
              <AdminPortal />
            </Suspense>
          </RequireAuth>
        }
      />

      {/* Public entry points into the Fanatic funnel — see FanaticEntryRedirect. */}
      <Route
        path={ROUTES.fanaticBrowse}
        element={<FanaticEntryRedirect view={FANATIC_VIEWS.browse} />}
      />
      <Route
        path={ROUTES.fanaticInviteAccept}
        element={<FanaticEntryRedirect view={FANATIC_VIEWS.browse} />}
      />

      <Route
        element={
          <RequireAuth allowedPersonas={['fanatic']}>
            <AppShell portalLabel="Fanatic portal" hasTenantNav={false} />
          </RequireAuth>
        }
      >
        {/* Fanatic portal. The shell owns the team switcher and the grant list;
            every child view reads the selected team from its context. */}
        <Route path={ROUTES.fanaticPortal} element={<FanaticPortalPage />}>
          <Route index element={<FanaticHomePage />} />
          <Route path={FANATIC_VIEWS.gameSummary} element={<FanaticGameSummaryPage />} />
          <Route path={FANATIC_VIEWS.liveVideo} element={<FanaticLiveVideoPage />} />
          <Route path={FANATIC_VIEWS.browse} element={<FanaticBrowseFollowPage />} />
          <Route path={FANATIC_VIEWS.account} element={<FanaticAccountSettingsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>

      {/* Documentation site. Static above `/app/:tenantSlug`, so React Router's
          ranking matches `/app/docs/play-entry` here rather than treating
          "docs" as a team code. Every authenticated persona may read it; the
          ToC itself is persona-scoped. */}
      <Route
        element={
          <RequireAuth>
            <AppShell portalLabel="Documentation" hasTenantNav={false} />
          </RequireAuth>
        }
      >
        <Route path={ROUTES.docs} element={<DocsPage />} />
        <Route path={`${ROUTES.docs}/:slug`} element={<DocsPage />} />
      </Route>

      <Route
        path="/app/:tenantSlug"
        element={
          <RequireAuth>
            <RequireTenantAccess>
              <AppShell />
            </RequireTenantAccess>
          </RequireAuth>
        }
      >
        {/* Portal home: active-event card, recent events, persona quick
            links. Every persona's landing surface when no deep link is used. */}
        <Route index element={<HomePage />} />
        {/* First-session setup wizard (Zone 2). Team Admin only; skippable. */}
        <Route path="welcome" element={<SetupWizardPage />} />
        {/* Roster Manager. Readable by every tenant persona; the page itself
            hides the write and delete affordances the gateway would refuse. */}
        <Route path={APP_VIEWS.roster} element={<RosterManagerPage />} />
        {/* Roster sits in the sidebar's "Team" group next to Team Users
            (`team/users`) and Team Settings (`team/settings`), so `team/roster`
            is a natural guess when typing or sharing a link. Redirect it to the
            canonical route instead of a 404 inside the shell. */}
        <Route path="team/roster" element={<Navigate to={`../${APP_VIEWS.roster}`} replace />} />
        {/* Event Manager + detail. Readable by every tenant persona; the pages
            hide the lifecycle and participation writes the service would refuse. */}
        <Route path={APP_VIEWS.events} element={<EventManagerPage />} />
        {/* Play Entry. Every tenant persona may open it; the page itself locks
            submission for read-only roles and for a non-active event. */}
        <Route
          path={APP_VIEWS.playEntry}
          element={
            <Suspense fallback={routeLoading}>
              <PlayEntryPage />
            </Suspense>
          }
        />
        {/* Play Entry field reference: the inline tooltips the laptop layout
            drops live here, one screen for every field in every phase. */}
        <Route path={APP_VIEWS.playEntryHelp} element={<PlayEntryHelpPage />} />
        {/* Play Archive. Readable by every tenant persona; the page hides the
            resolve and edit affordances the service would refuse. */}
        <Route path={APP_VIEWS.playArchive} element={<PlayArchivePage />} />
        {/* Coaches (O/D) Highlight. Every tenant persona may read it; the O/D
            toggle simply opens on the coordinator's own side. */}
        <Route
          path={APP_VIEWS.coachesHighlight}
          element={
            <Suspense fallback={routeLoading}>
              <CoachesHighlightPage />
            </Suspense>
          }
        />
        {/* Game Summary. Readable by every tenant persona including `fanatic`,
            who gets the team-aggregate projection of the stat blocks. */}
        <Route path={APP_VIEWS.gameSummary} element={<GameSummaryPage />} />
        {/* Stat Tracker. Team members only — the page renders the 403 for a
            fanatic itself rather than bouncing them to a different view. */}
        <Route path={APP_VIEWS.statTracker} element={<StatTrackerPage />} />
        {/* Live Video (Zone 3): dedicated video-first page with the persistent
            stat strip. In the nav model, so reachable from every core page. */}
        <Route path={APP_VIEWS.liveVideo} element={<LiveVideoPage />} />
        {/* Playbook. Every tenant persona except `fanatic` may read it; stat
            operators see it without write affordances, and the page renders the
            403 / 402 panels itself rather than bouncing to another view. */}
        <Route path={APP_VIEWS.playbook} element={<PlaybookPage />} />
        <Route path={`${APP_VIEWS.events}/:eventId`} element={<EventDetailPage />} />
        {/* Team administration. Guarded again here: the parent layout admits
            every tenant persona, this view admits only administrators. */}
        <Route
          path={APP_VIEWS.teamUsers}
          element={
            <RequireAuth allowedPersonas={['team_admin', 'platform_admin']}>
              <UserManagementPage />
            </RequireAuth>
          }
        />
        {/* Billing portal (FR-BILLING-01/02/05/06). Team Admin only: the
            billing endpoints are admin-scoped, so a coach would only ever see
            a 403 here. */}
        <Route
          path={APP_VIEWS.billing}
          element={
            <RequireAuth allowedPersonas={['team_admin', 'platform_admin']}>
              <BillingPortal />
            </RequireAuth>
          }
        />
        {/* Team Settings: general, video provider, support email, Success
            Measure (FR-TENANT-05, FR-VIDEO-03..05, FR-HELP-07, FR-PLAY-07).
            Team Admin only — every endpoint behind it is admin-scoped. */}
        <Route
          path={APP_VIEWS.teamSettings}
          element={
            <RequireAuth allowedPersonas={['team_admin', 'platform_admin']}>
              <TeamSettings />
            </RequireAuth>
          }
        />
        {/* Team Admin → Fanatics: access mode, follower list, invites, revoke
            (AC-FANATIC-009..011). Team Admin only — the grant endpoints are
            admin-scoped. */}
        <Route
          path={APP_VIEWS.teamFanatics}
          element={
            <RequireAuth allowedPersonas={['team_admin', 'platform_admin']}>
              <FanaticManagementPage />
            </RequireAuth>
          }
        />
        {/* Integrations: Hudl bulk-stat CSV export + column-mapping presets
            (FR-HUDL-01..05). Team Admin only; the whole /hudl/v1 surface is
            admin-scoped at the gateway and Varsity+ at the service. */}
        <Route
          path={APP_VIEWS.teamIntegrations}
          element={
            <RequireAuth allowedPersonas={['team_admin', 'platform_admin']}>
              <IntegrationsPage />
            </RequireAuth>
          }
        />
        {/* SAML 2.0 / SCIM 2.0 federation + per-persona provider policy
            (FR-AUTH-01/02/06). Team Admin only; Elite-gated at the service. */}
        <Route
          path={APP_VIEWS.teamIdentityProviders}
          element={
            <RequireAuth allowedPersonas={['team_admin', 'platform_admin']}>
              <IdentityProvidersPage />
            </RequireAuth>
          }
        />
        {/* Custom stat schema registry (FR-STATS-01..04). Team Admin only. */}
        <Route
          path={APP_VIEWS.teamStatFields}
          element={
            <RequireAuth allowedPersonas={['team_admin', 'platform_admin']}>
              <CustomStatFieldsPage />
            </RequireAuth>
          }
        />
        {/* The signed-in user's own preferences: theme,
            password reset, two-factor, sign-in methods (FR-AUTH-05) and
            session revocation (FR-AUTH-04). Every tenant persona has one. */}
        <Route path={APP_VIEWS.settings} element={<PreferencesPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
